import { trimStart } from 'lodash';
import parseDomain from 'parse-domain';
import qs from 'qs';

/**
 * Returns an object after parsing a query string of a url
 * @param {string} queryString
 * @returns {object} parsed object
 * @example parseQueryParams('?id=1234&param=hello') will return { id: '1234', param: 'hello' }
 */
export function parseQueryParams(queryString) {
  const paramString = queryString.split('?').pop();
  return qs.parse(paramString);
}

function isUrlMatching(url, pattern) {
  const { subdomain } = parseDomain(url) || {};

  if (subdomain) {
    if (subdomain.match(pattern)) {
      return true;
    }
  }

  return false;
}

/**
 * Determine if any brand is known to be associated with a given URL.  If so, return
 * the name of that brand; if not, return 'decisiv'.  If the given environment (which
 * defaults to `process.env` provides a `BRAND`, use that instead of even looking at
 * the URL.
 *
 * @param {string} url The URL to examine for brand-ness
 * @param {object} env The environment, which may provide a `BRAND` override.
 * @returns {string}
 */
export function getBrandFromUrl(url = window.location.href, env = process.env) {
  // To enable a better developer experience, prefer the BRAND set at boot up.
  if (env.BRAND && env.BRAND !== 'default') {
    return env.BRAND;
  }

  switch (true) {
    case isUrlMatching(url, /michelin/):
      return 'michelin';
    default:
      return 'decisiv';
  }
}

/**
 * Determine if any tenant is known to be associated with a given URL.  If so, return
 * the name of that tenant; if not, return 'vlv'.  If the given environment (which
 * defaults to `process.env` provides a `TENANT`, use that instead of even looking at
 * the URL.
 *
 * TODO: change the default from `vlv` to `dcv` once we have a Volvo-brand URL that maps to `vlv`
 *
 * @param {string} url The URL to examine for a tenant association
 * @param {object} env The environment, which may provide a `TENANT` override.
 * @returns {string}
 */
export function getTenantFromUrl(url = window.location.href, env = process.env) {
  // To enable a better developer experience, prefer the TENANT set at boot up.
  if (env.TENANT && env.TENANT !== 'default') {
    return env.TENANT;
  }

  switch (true) {
    case isUrlMatching(url, /michelin/):
      return 'mich';
    default:
      return 'vlv'; // TODO: change this to 'dcv' once we have a Volvo-brand URL
  }
}

/**
 * Given a base URL, a path, and optionally some params, assemble it all into a
 * final properly-encoded URL string.
 * @param {string} base - The URL base, e.g. 'https://www.google.com'
 * @param {string} path - A path at the destination server, e.g. '/search'
 * @param {object} params - Optional params, e.g. { q: 'lmgtfy' }
 * @returns {string} The final URL, e.g. 'https://www.google.com/search?q=lmgtfy'
 */
export function makeUrl(base, path, params = {}) {
  const endpoint = `${base}/${trimStart(path, '/')}`;
  const queryString = qs.stringify(params);
  return `${endpoint}${(queryString && `?${queryString}`)}`;
}

/**
 * Some older versions of various browsers do not support window.location.origin.
 * This is a re-implementation that should work cross-browser to return the same
 * result.
 * @returns {string}
 */
/* istanbul ignore next */
export function locationOrigin() {
  if (window.location.origin) {
    return window.location.origin;
  }
  const port = window.location.port && `:${window.location.port}`;
  return `${window.location.protocol}//${window.location.hostname}${port}`;
}




// WEBPACK FOOTER //
// ./utils/url.js